import { Dispatch, ReactElement, SetStateAction, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useUserPermissions } from 'auth/hooks';
import { ImportType } from 'bom/enums';
import { AgGridTable, Confirmation } from 'core/components';
import { NotificationPosition } from 'core/enums';
import { AlternateVersionHelper } from 'core/helpers';
import { useAgGridTableContext } from 'core/states';
import { IConfirmation, ISelectionAction, ITableAction } from 'core/types';
import { AddPricingProfile } from 'pricing/components';
import { IPricingProfile } from 'pricing/types';
import { QuotePartAddNote } from 'quotes/components/QuotePartAddNote';
import QuotePartAddToPricingProfileForm from 'quotes/components/QuotePartAddToPricingProfileForm';
import { QuotePartAddToQuote } from 'quotes/components/QuotePartAddToQuote';
import { QuotePartAssignVendor } from 'quotes/components/QuotePartAssignVendor';
import QuotePartCostDiscountForm from 'quotes/components/QuotePartCostDiscountForm';
import { QuotePartCreateCustomerOrder } from 'quotes/components/QuotePartCreateCustomerOrder';
import { QuotePartListAction } from 'quotes/components/QuotePartList';
import QuotePartMoveToForm from 'quotes/components/QuotePartMoveToForm';
import { QuotePartRemoveNotes } from 'quotes/components/QuotePartRemoveNotes';
import QuotePartResetPricingForm from 'quotes/components/QuotePartResetPricingForm';
import { QuotePartsImportBom } from 'quotes/components/QuotePartsImportBom';
import { QuotePartUpdateBulkForm } from 'quotes/components/QuotePartUpdateBulkForm';
import QuotePartUpdateMarginForm from 'quotes/components/QuotePartUpdateMarginForm';
import { QuotePartUpsertLine } from 'quotes/components/QuotePartUpsertLine';
import QuoteVersionReleaseForm from 'quotes/components/QuoteVersionReleaseForm';
import { QuoteEntityType, QuotePartListSelectionAction, QuoteType } from 'quotes/enums';
import { useCustomerQuotePartsContext } from 'quotes/states';
import { ICustomerQuote } from 'quotes/types';

import { QuotePartListEmptyState } from './QuotePartListEmptyState';

interface IProps {
  quote: ICustomerQuote;
  actions?: () => ITableAction[];
  actionButtons?: ReactElement[];
  activeActionDrawer?: QuotePartListAction;
  setActiveActionDrawer: Dispatch<SetStateAction<QuotePartListAction>>;
  onCloseActionDrawer?: () => void;
  selectionActions?: () => ISelectionAction[];
  activeSelectionActionDrawer?: QuotePartListSelectionAction;
  setActiveSelectionActionDrawer?: Dispatch<SetStateAction<QuotePartListSelectionAction>>;
  onCloseSelectionActionDrawer?: (closeSelection?: boolean) => void;
  closeSelection?: boolean;
  setCloseSelection?: Dispatch<SetStateAction<boolean>>;
  confirmationOption?: IConfirmation;
  setConfirmationOption?: Dispatch<SetStateAction<IConfirmation>>;
  defaultPricingProfile?: IPricingProfile;
  setDefaultPricingProfile?: Dispatch<SetStateAction<IPricingProfile>>;
  onPricingProfileSubmitSuccess: (pricingProfile: IPricingProfile) => void;
  onPricingProfileFormClose: () => void;
  quoteEntityType: QuoteEntityType;
}

const IMPORT_BOM_FILE_ID_KEY = 'importBomFileId';

const QuotePartListTableView = ({
  quote,
  actions,
  actionButtons,
  activeActionDrawer,
  setActiveActionDrawer,
  onCloseActionDrawer,
  selectionActions,
  activeSelectionActionDrawer,
  setActiveSelectionActionDrawer,
  onCloseSelectionActionDrawer,
  closeSelection,
  setCloseSelection,
  confirmationOption,
  setConfirmationOption,
  defaultPricingProfile,
  setDefaultPricingProfile,
  onPricingProfileSubmitSuccess,
  onPricingProfileFormClose,
  quoteEntityType,
}: IProps) => {
  const [searchParams, setSearchParams] = useSearchParams();

  const { selectedRows, deselectAllRows } = useAgGridTableContext();
  const { setDraftVersionChanged } = useCustomerQuotePartsContext();

  const { hasLlmAccess } = useUserPermissions();

  const isAlternateVersionEnabled = AlternateVersionHelper.isAlternateVersionEnabled({ quote });

  const importBomFileId = searchParams.get(IMPORT_BOM_FILE_ID_KEY);

  useEffect(() => {
    if (importBomFileId) {
      setActiveActionDrawer(QuotePartListAction.IMPORT);
    }
  }, [importBomFileId, setActiveActionDrawer]);

  const onImportBomCompleted = () => {
    setDraftVersionChanged();

    setSearchParams((prev) => {
      prev.delete(IMPORT_BOM_FILE_ID_KEY);

      return prev;
    });
  };

  return (
    <>
      <AgGridTable
        actions={actions}
        actionButtons={actionButtons}
        selectionActions={selectionActions}
        closeSelection={closeSelection}
        setCloseSelection={setCloseSelection}
        progressBarStyle={quote?.freightEnabled ? { bottom: 96 } : {}}
        className="statusBarLeftFullWidth"
        emptyStateElement={hasLlmAccess ? <QuotePartListEmptyState quote={quote} /> : undefined}
      />

      <QuotePartAddNote
        isOpen={QuotePartListSelectionAction.ADD_NOTE === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        quoteParts={selectedRows}
        onSubmitSuccess={setDraftVersionChanged}
        entityType={quoteEntityType}
        notificationPosition={NotificationPosition.TOP}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
        quoteType={QuoteType.CUSTOMER}
      />

      <QuotePartRemoveNotes
        isOpen={QuotePartListSelectionAction.REMOVE_NOTES === activeSelectionActionDrawer}
        quoteParts={selectedRows}
        onClose={onCloseSelectionActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        notificationPosition={NotificationPosition.TOP}
      />

      <QuotePartResetPricingForm
        isOpen={QuotePartListSelectionAction.RESET_PRICING === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
      />

      <QuotePartCostDiscountForm
        isOpen={QuotePartListSelectionAction.APPLY_COST_DISCOUNT === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
      />

      <QuotePartAddToQuote
        key="addToCustomerQuote"
        addToQuoteType={
          QuotePartListSelectionAction.ADD_TO_CUSTOMER_QUOTE === activeSelectionActionDrawer
            ? QuoteType.CUSTOMER
            : null
        }
        onClose={onCloseSelectionActionDrawer}
        quote={quote}
        quoteParts={selectedRows}
        onSubmitSuccess={deselectAllRows}
      />

      <QuotePartAddToQuote
        key="addToVendorQuote"
        addToQuoteType={
          QuotePartListSelectionAction.ADD_TO_VENDOR_QUOTE === activeSelectionActionDrawer
            ? QuoteType.VENDOR
            : null
        }
        onClose={onCloseSelectionActionDrawer}
        quote={quote}
        quoteParts={selectedRows}
        onSubmitSuccess={deselectAllRows}
      />

      <QuotePartAddToQuote
        key="addToCustomerOrder"
        addToQuoteType={
          QuotePartListSelectionAction.ADD_TO_CUSTOMER_ORDER === activeSelectionActionDrawer
            ? QuoteType.CUSTOMER
            : null
        }
        onClose={onCloseSelectionActionDrawer}
        quote={quote}
        quoteParts={selectedRows}
        onSubmitSuccess={deselectAllRows}
        entityType={QuoteEntityType.ORDER}
      />

      <QuotePartAddToQuote
        key="addToVendorOrder"
        addToQuoteType={
          QuotePartListSelectionAction.ADD_TO_VENDOR_ORDER === activeSelectionActionDrawer
            ? QuoteType.VENDOR
            : null
        }
        onClose={onCloseSelectionActionDrawer}
        quote={quote}
        quoteParts={selectedRows}
        onSubmitSuccess={deselectAllRows}
        entityType={QuoteEntityType.ORDER}
      />

      <QuotePartAddToPricingProfileForm
        isOpen={QuotePartListSelectionAction.ADD_TO_PRICING_PROFILE === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        setConfirmationOption={setConfirmationOption}
        setActiveSelectionActionDrawer={setActiveSelectionActionDrawer}
        defaultPricingProfile={defaultPricingProfile}
        setDefaultPricingProfile={setDefaultPricingProfile}
        onSubmitSuccess={setDraftVersionChanged}
        quoteEntityType={quoteEntityType}
      />

      <QuotePartCreateCustomerOrder
        isOpen={
          QuotePartListSelectionAction.CREATE_ORDER === activeSelectionActionDrawer ||
          QuotePartListAction.CREATE_ORDER === activeActionDrawer
        }
        onClose={activeSelectionActionDrawer ? onCloseSelectionActionDrawer : onCloseActionDrawer}
        quote={quote}
        isSelectionAction={!!activeSelectionActionDrawer}
      />

      <QuotePartMoveToForm
        isOpen={QuotePartListSelectionAction.MOVE_TO === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        quoteId={quote?.id}
        onSubmitSuccess={setDraftVersionChanged}
      />

      <QuoteVersionReleaseForm
        isOpen={QuotePartListAction.RELEASE_VERSION === activeActionDrawer}
        onClose={onCloseActionDrawer}
        quote={quote}
        entityType={quoteEntityType}
      />

      <QuotePartUpdateMarginForm
        isOpen={QuotePartListSelectionAction.UPDATE_MARGIN === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        quote={quote}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      <QuotePartUpsertLine
        isOpen={
          QuotePartListAction.ADD_LINE === activeActionDrawer ||
          QuotePartListSelectionAction.ADD_ALTERNATE_PART === activeSelectionActionDrawer
        }
        onClose={activeSelectionActionDrawer ? onCloseSelectionActionDrawer : onCloseActionDrawer}
        quote={quote}
        alternate={QuotePartListSelectionAction.ADD_ALTERNATE_PART === activeSelectionActionDrawer}
        onSubmitSuccess={() => {
          activeSelectionActionDrawer && onCloseSelectionActionDrawer(true);
          setDraftVersionChanged();
        }}
        entityType={quoteEntityType}
      />

      <AddPricingProfile
        isOpen={QuotePartListSelectionAction.CREATE_PRICING_PROFILE === activeSelectionActionDrawer}
        onClose={onPricingProfileFormClose}
        onSubmitSuccess={onPricingProfileSubmitSuccess}
        level={1}
        defaultName={defaultPricingProfile?.name}
      />

      <QuotePartsImportBom
        isOpen={QuotePartListAction.IMPORT === activeActionDrawer}
        onClose={onCloseActionDrawer}
        quote={quote}
        type={ImportType.CUSTOMER_QUOTE}
        fileId={importBomFileId}
        onSubmitSuccess={onImportBomCompleted}
      />

      <QuotePartUpdateBulkForm
        isOpen={QuotePartListSelectionAction.UPDATE_PARTS === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      <QuotePartAssignVendor
        isOpen={QuotePartListSelectionAction.ASSIGN_VENDOR === activeSelectionActionDrawer}
        onClose={onCloseSelectionActionDrawer}
        onSubmitSuccess={setDraftVersionChanged}
        isAlternateVersionEnabled={isAlternateVersionEnabled}
      />

      {confirmationOption && <Confirmation isOpen={true} {...confirmationOption} />}
    </>
  );
};

export default QuotePartListTableView;
